<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="$emit('close')">
      <div class="howto guide app-menu" role="dialog" aria-modal="true" aria-label="Menu">
        <button class="howto__close icon-button" aria-label="Close" @click="$emit('close')">×</button>
        <div class="howto__body">
          <h3>Menu</h3>
          <div v-if="roomCode" class="app-menu__code">
            <span class="app-menu__code-label">{{ isTable ? 'This table is showing' : 'Room code' }}</span>
            <strong>{{ roomCode }}</strong>
            <button class="linkish" @click="copy">{{ copyLabel }}</button>
            <span class="app-menu__code-sub">Phone link + TV link, together.</span>
          </div>
          <!-- Playtest mode (2026-09-25): Claude listens and fixes while you play. -->
          <div v-if="playtest" class="app-menu__playtest">
            <button class="help-menu__item" @click="$emit('playtest-toggle')">
              <span class="help-menu__name">Playtest mode: {{ playtest.on ? 'on' : 'off' }}</span>
              <span class="help-menu__sub">{{ playtest.on ? 'Each phone can send what it hears to Claude, who fixes things while you play. Tap to switch off for everyone.' : 'Switch on for this game: each player can then turn on their own mic, and Claude reads along and fixes things as you play.' }}</span>
            </button>
            <button v-if="playtest.on" class="help-menu__item" :disabled="playtest.mic === 'unsupported'" @click="$emit('mic-toggle')">
              <span class="help-menu__name">My microphone: {{ playtest.mic === 'on' ? 'on' : 'off' }}</span>
              <span class="help-menu__sub">{{ micNote }}</span>
            </button>
          </div>
          <ul class="help-menu__list">
            <li>
              <button class="help-menu__item" @click="$emit('help')">
                <span class="help-menu__name">Help</span>
                <span class="help-menu__sub">How to play, your role, what the words mean.</span>
              </button>
            </li>
            <li v-if="!isTable">
              <button class="help-menu__item" @click="toggleConfirm">
                <span class="help-menu__name">Confirm every action: {{ settings.confirm ? 'on' : 'off' }}</span>
                <span class="help-menu__sub">{{ settings.confirm ? `A dialog says the exact cost and gain before anything happens.${settings.skip.length ? ` Skipped for ${settings.skip.length} kind${settings.skip.length === 1 ? '' : 's'} of action.` : ''}` : 'Actions happen at a tap. Turn it on to be shown the cost and gain first.' }}</span>
              </button>
              <button v-if="settings.confirm && settings.skip.length" class="linkish app-menu__again" @click="askAgainForAll">Ask again for everything</button>
            </li>
            <li v-if="!isTable">
              <button class="help-menu__item" @click="toggleOutcome">
                <span class="help-menu__name">Show what happened: {{ settings.outcome ? 'on' : 'off' }}</span>
                <span class="help-menu__sub">{{ settings.outcome ? 'After every action a card says what it cost, what it made and what the dice did.' : 'Actions just happen; the ledger keeps the record. Turn it on to be shown each result.' }}</span>
              </button>
            </li>
            <li>
              <button class="help-menu__item" @click="reload">
                <span class="help-menu__name">Reload</span>
                <span class="help-menu__sub">Fetch the newest version of the game.</span>
              </button>
            </li>
            <li v-if="canLeave">
              <button class="help-menu__item" @click="$emit('leave')">
                <span class="help-menu__name">{{ leaveLabel }}</span>
                <span class="help-menu__sub">{{ isTable ? 'The game carries on without this screen.' : 'The others carry on without you.' }}</span>
              </button>
            </li>
          </ul>
          <AdminPanel v-if="isAdmin" />
        </div>
        <div class="howto__nav">
          <span class="howto__dots" />
          <button class="secondary howto__next" @click="$emit('close')">Close</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { shareRoomLinks } from '@/utils/roomLinks.js';
import AdminPanel from '@/components/auth/AdminPanel.vue';
import { settings, setConfirm, askAgainForAll, setOutcome } from '@/settings';

// Everything the old title bar was carrying, behind one button. Matt:
// "let's get rid of the header bar that says Commonwealth, because that's
// not really useful... let's make it a single button up there that opens a
// menu that has things like the code and the question mark and the reload
// button and all that, and try and reclaim that space."
export default {
  name: 'AppMenu',
  components: { AdminPanel },
  props: {
    roomCode: { type: String, default: '' },
    isTable: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    canLeave: { type: Boolean, default: false },
    leaveLabel: { type: String, default: 'Leave this game' },
    playtest: { type: Object, default: null },
  },
  emits: ['close', 'help', 'leave', 'playtest-toggle', 'mic-toggle'],
  computed: {
    micNote() {
      const m = this.playtest?.mic;
      if (m === 'on') return `Sending what this phone hears (${this.playtest.sent} sentences so far). Tap to stop.`;
      if (m === 'paused') return 'Paused (a reload or a long silence). The next tap anywhere starts it again.';
      if (m === 'denied') return 'The microphone was refused. Allow it for this site in Settings, then try again.';
      if (m === 'unsupported') return 'This browser cannot transcribe speech. Safari on an iPhone can.';
      return 'Tap to start. Only what you say while it is on is sent, as text.';
    },
  },
  data() { return {
      settings, copyLabel: 'Share the links' }; },
  methods: {
    toggleConfirm() { setConfirm(!settings.confirm); },
    toggleOutcome() { setOutcome(!settings.outcome); },
    askAgainForAll,
    reload() { window.location.reload(); },
    async copy() {
      try {
        if ((await shareRoomLinks(this.roomCode)) === 'copied') {
          this.copyLabel = 'Copied both';
          window.setTimeout(() => { this.copyLabel = 'Share the links'; }, 1800);
        }
      } catch { this.copyLabel = 'Share the links'; }
    },
  },
};
</script>

<style scoped>
.app-menu__code-sub { display: block; font-size: 0.8rem; opacity: 0.65; }
</style>
